import {
    Container,
    Row,
    Col,
    Form,
    InputGroup,
    FloatingLabel,
    Button,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import React, { useState } from 'react';

import SEO from '@/components/common/SEO';
import { useAuth } from '@/shared/contexts/AuthContext';
import emailIcon from '@/assets/img/icons/svg/emailIcon.svg';

import './ForgotPassword.css';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [touched, setTouched] = useState(false);

    const { resetPassword } = useAuth();

    const validateEmail = (email) => {
        const emailRegex = /^\S+@\S+\.\S+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setTouched(true);
        setError('');
        setMessage('');

        if (!email.trim()) {
            setError('Please enter your email address.');
            return;
        }

        if (!validateEmail(email)) {
            setError('Please provide a valid email address.');
            return;
        }

        try {
            setLoading(true);

            await resetPassword(email);

            setMessage(
                'If we have any account with this email address, you will receive a password reset email. Check your inbox and spam folder!'
            );
            setEmail('');
            setTouched(false);
        } catch (error) {
            let errorMessage = 'Failed to send password reset email.';

            if (error.code === 'auth/user-not-found') {
                errorMessage = 'No account found with this email address.';
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = 'Invalid email address format.';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage =
                    'Too many password reset attempts. Please try again later.';
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Reset Password"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Reset Password', path: '/forgot-password' },
                ]}
            />
            <div id="forgot-password" className="page-container bg-primary">
                <Container>
                    <Form
                        noValidate
                        onSubmit={handleSubmit}
                        className="forgot-password-form d-flex flex-column align-items-center bg-white gap-3 mx-auto w-100 py-5 px-3 rounded-4 shadow"
                    >
                        <Row className="mb-1 w-100">
                            <Col xs={12}>
                                <h1 className="text-primary text-center mb-0">
                                    Reset Your Password
                                </h1>
                            </Col>
                        </Row>

                        <Row className="mb-3 w-100">
                            <Col xs={12}>
                                <p className="text-center text-muted fs-5 lh-base mb-0">
                                    Enter your email address and we&apos;ll send
                                    you a link to reset your password.
                                </p>
                            </Col>
                        </Row>

                        <Row className="justify-content-center w-100">
                            <Form.Group
                                as={Col}
                                xl="8"
                                lg="10"
                                md="12"
                                sm="12"
                                className="mb-3 d-flex justify-content-center"
                            >
                                <InputGroup
                                    hasValidation
                                    className="input-logo-label w-100"
                                >
                                    <img
                                        src={emailIcon}
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    <FloatingLabel
                                        controlId="floatingEmail"
                                        label="Email"
                                    >
                                        <Form.Control
                                            type="email"
                                            placeholder="name@example.com"
                                            name="email"
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                            onBlur={() => setTouched(true)}
                                            isInvalid={
                                                touched &&
                                                (!email.trim() ||
                                                    !validateEmail(email))
                                            }
                                            isValid={
                                                touched &&
                                                email.trim() &&
                                                validateEmail(email)
                                            }
                                            disabled={loading}
                                        />
                                    </FloatingLabel>
                                </InputGroup>
                            </Form.Group>
                        </Row>

                        {/* Alerts row positioned below the input field */}
                        <Row className="justify-content-center w-100">
                            <Col
                                xl="8"
                                lg="10"
                                md="12"
                                sm="12"
                                className="d-flex justify-content-center"
                            >
                                {message && !error && !touched && (
                                    <div
                                        className="alert alert-success mt-1 mb-0 w-100"
                                        role="alert"
                                    >
                                        <strong>Success!</strong> {message}
                                    </div>
                                )}

                                {error && (
                                    <div
                                        className="alert alert-danger mt-1 mb-0 w-100"
                                        role="alert"
                                    >
                                        <strong>Error!</strong> {error}
                                    </div>
                                )}

                                {!message &&
                                    !error &&
                                    touched &&
                                    !email.trim() && (
                                        <div
                                            className="alert alert-warning mt-1 mb-0 w-100"
                                            role="alert"
                                        >
                                            <strong>Warning!</strong> Please
                                            enter your email address.
                                        </div>
                                    )}

                                {!message &&
                                    !error &&
                                    touched &&
                                    email.trim() &&
                                    !validateEmail(email) && (
                                        <div
                                            className="alert alert-warning mt-1 mb-0 w-100"
                                            role="alert"
                                        >
                                            <strong>Warning!</strong> Please
                                            provide a valid email address.
                                        </div>
                                    )}
                            </Col>
                        </Row>

                        <Row className="mt-2 w-100">
                            <Col
                                xs={12}
                                className="d-flex justify-content-center"
                            >
                                <Button
                                    className="reset-btn btn-lg bg-primary border-0 text-white fw-bold py-3 px-5 hover-effect"
                                    type="submit"
                                    disabled={loading}
                                >
                                    {loading
                                        ? 'Sending...'
                                        : 'Send Reset Email'}
                                </Button>
                            </Col>
                        </Row>

                        <Row className="mt-3 w-100">
                            <Col xs={12} className="text-center">
                                <span className="text-muted">
                                    Remember your password?{' '}
                                </span>
                                <Link
                                    to="/login"
                                    className="text-info fw-bold text-decoration-none ms-1"
                                >
                                    Back to Log In
                                </Link>
                            </Col>
                        </Row>
                    </Form>
                </Container>
            </div>
        </>
    );
};

export default ForgotPassword;
